import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Search, ArrowRight, CheckCircle2 } from "lucide-react";

import OptibizCrmImg from "@/assets/Optibizcrm.png";
import HRMIconImg from "@/assets/HRM-Icon.png";
import PosIconImg from "@/assets/Asset 10.png";
import EduIconImg from "@/assets/optibizedu.png";
import DTIconImg from "@/assets/DT-Icon.png";
import HoteliImg from "@/assets/Hoteli.png";

const isExternalUrl = (value: string) => /^https?:\/\//i.test(value);

const applications = [
  {
    id: "crm",
    name: "OptiBiz CRM",
    category: "Sales",
    description: "Build lasting relationships with your customers and grow your business.",
    features: ["Lead Management", "Sales Pipeline", "Email Integration", "Customer Analytics"],
    color: "bg-optibiz-blue",
    textColor: "text-optibiz-blue",
    lightColor: "bg-blue-50",
    href: "https://optibizcrm.co.ke/",
    icon: OptibizCrmImg
  },
  {
    id: "pos",
    name: "OptiBiz POS",
    category: "Retail",
    description: "Modern POS solution for retail businesses with inventory management.",
    features: ["Multi-location Support", "Inventory Tracking", "Sales Analytics", "Payment Integration"],
    color: "bg-optibiz-orange",
    textColor: "text-optibiz-orange",
    lightColor: "bg-orange-50",
    href: "https://optibizpos.co.ke/",
    icon: PosIconImg
  },
  {
    id: "edu",
    name: "OptiBiz EDU",
    category: "Education",
    description: "Comprehensive school management solution for modern educational institutions.",
    features: ["Student Information System", "Gradebook & Reporting", "Parent Portal", "Fee Management"],
    color: "bg-optibiz-yellow",
    textColor: "text-optibiz-yellow",
    lightColor: "bg-yellow-50",
    href: "https://optibizedu.com/",
    icon: EduIconImg
  },
  {
    id: "renttoday",
    name: "RentToday",
    category: "Property Management",
    description: "Manage properties, tenants and rent collection with ease.",
    features: ["Property & Unit Management", "Tenant Records", "Rent Collection", "Payment Reminders"],
    color: "bg-optibiz-green",
    textColor: "text-optibiz-green",
    lightColor: "bg-green-50",
    href: "https://renttoday.co.ke/",
    icon: HRMIconImg
  },
  {
    id: "care",
    icon: DTIconImg,
    name: "OptiBiz CARE",
    colorClass: "text-optibiz-magenta",
    bgClass: "bg-optibiz-magenta/10",
    tagline: "Hospital Management System",
    description: "Transform your data into actionable insights with powerful analytics for hospital operations.",
    features: ["Patient Management", "Appointment Scheduling", "Inventory Tracking", "Reporting & Analytics"],
    category: "Hospital",
    link: "https://optibizcare.co.ke",
    textColor: "text-tasktide-blue",
    lightColor: "bg-gray-100"
  },
  {
    id: "hoteli",
    icon: HoteliImg,
    name: "OptiBiz Hoteli",
    colorClass: "text-optibiz-yellow",
    bgClass: "bg-optibiz-yellow/10",
    tagline: "Restaurant Management System",
    description: "Streamline restaurant operations with our comprehensive management system.",
    features: ["Menu Management", "Order Processing", "Inventory Tracking", "Reporting & Analytics"],
    category: "Restaurant",
    link: "https://hoteli.co.ke",
    textColor: "text-tasktide-orange",
    lightColor: "bg-gray-100"
  },
];

const Applications = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedApp, setSelectedApp] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const appRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  useEffect(() => {
    const appParam = searchParams.get("app");
    if (appParam) {
      setSearchQuery(appParam);
      setSelectedApp(appParam);
      
      // Scroll to the selected app card with a small delay to ensure DOM is ready
      setTimeout(() => {
        const element = appRefs.current[appParam];
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    }
  }, [searchParams]);

  const filteredApps = applications.filter(app =>
    app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    app.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-tasktide-bg dark:bg-gray-900 font-sans flex flex-col">
      <Header />

      <main className="flex-1 pt-24">
        <section className="pt-4 pb-8 px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold text-tasktide-dark dark:text-white mb-4">
            Explore Our Applications
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8">
            An integrated suite of business tools tailored for African enterprises — manage sales, people, retail, education and more from one platform.
          </p>

          <div className="flex items-center justify-center gap-4 flex-col sm:flex-row">
            <div className="flex items-center gap-4">
              <Link to="/contact" className="inline-flex items-center gap-3 bg-optibiz-navy dark:bg-optibiz-orange text-white px-6 py-3 rounded-full font-semibold hover:opacity-95">
                Request a Demo <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="mt-4 sm:mt-0 w-full sm:w-auto">
              <div className="max-w-md mx-auto relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                <Input
                  placeholder="Search applications..."
                  className="pl-12 pr-4 py-3 rounded-full border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white text-base shadow-sm w-full"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 px-4 md:px-8 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredApps.map((app) => (
              <article 
                key={app.id} 
                ref={(el) => { if (el) appRefs.current[app.id] = el; }}
                className="bg-white dark:bg-gray-800 p-6 md:p-8 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-all flex flex-col"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-14 h-14 rounded-lg ${app.lightColor} flex items-center justify-center ${app.textColor} shadow-sm`}>
                    {app.icon && (
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore -- imported image path
                      <img src={app.icon} alt={`${app.name} icon`} className="w-10 h-10 object-contain" />
                    )}
                  </div>
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full ${app.lightColor} ${app.textColor}`}>
                    {app.category}
                  </span>
                </div>

                <h3 className="text-2xl font-bold text-tasktide-dark dark:text-white mb-2">{app.name}</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">{app.description}</p>

                <ul className="grid grid-cols-1 gap-2 mb-6">
                  {app.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
                      <CheckCircle2 size={16} className={app.textColor} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto">
                  {(() => {
                    const target = app.href ?? app.link ?? "/contact";
                    const classes =
                      "w-full inline-flex items-center justify-center gap-2 bg-transparent border border-gray-200 dark:border-gray-700 text-optibiz-navy dark:text-optibiz-yellow hover:bg-optibiz-navy dark:hover:bg-optibiz-orange hover:text-white rounded-lg py-3 font-semibold transition-all";

                    if (isExternalUrl(target)) {
                      return (
                        <a href={target} target="_blank" rel="noopener noreferrer" className={classes}>
                          Visit Site <ArrowRight className="w-4 h-4" />
                        </a>
                      );
                    }

                    return (
                      <Link to={target} className={classes}>
                        Request Demo <ArrowRight className="w-4 h-4" />
                      </Link>
                    );
                  })()}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="bg-gradient-to-r from-optibiz-blue/8 to-optibiz-green/8 dark:from-optibiz-blue/20 dark:to-optibiz-green/20 rounded-2xl m-6 p-8 md:p-12 border border-gray-100 dark:border-gray-700 max-w-7xl mx-auto">
          <h3 className="text-2xl font-bold text-tasktide-dark dark:text-white mb-4">Who We Are</h3>
          <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed">
            Our integrated suite of applications helps organizations streamline operations, improve efficiency, and drive growth. Whether you're managing sales with OptiBiz CRM, managing properties and tenants with RentToday, processing retail transactions with OptiBiz POS, or running educational institutions with OptiBiz EDU, we provide specialized solutions tailored to your industry.
          </p>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default Applications;
